import { useSettingsService } from '~/common/services/SettingsService';
import { Language } from '~/common/entities/Settings/SettingsTypes';
import de from '~/common/i18n/generated/de.properties.json';
import en from '~/common/i18n/generated/en.properties.json';
import { useEffect } from 'react';

const acceptedLanguages = {
  de,
  en,
};

const weekdayAbbreviationGerman = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const weekdayAbbreviationEnglish = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const acceptedAbbreviation = {
  de: weekdayAbbreviationGerman,
  en: weekdayAbbreviationEnglish,
};

const monthsGerman = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const monthsEnglish = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const acceptedMonth = {
  de: monthsGerman,
  en: monthsEnglish,
};

type Parameters = string[];

function parameterize(params?: Parameters) {
  return (token: string) => {
    if (token.startsWith('#')) {
      const index = parseInt(token.charAt(1), 10);
      if (!params || params.length - 1 < index) {
        return token;
      }
      return params[index];
    }
    return token;
  };
}

type TranslationIndices = keyof (typeof acceptedLanguages)['de'] &
  keyof (typeof acceptedLanguages)['en'];

function valid<T>(text: T, language: Language) {
  return (
    !!text &&
    !!acceptedLanguages[language] &&
    typeof text === 'string' &&
    !!acceptedLanguages[language][text as TranslationIndices]
  );
}

export function translate<T>(
  text: T,
  language: Language,
  params?: Parameters,
): T | string | undefined {
  if (!language) return undefined;
  if (!valid(text, language)) {
    return text;
  }

  const translation = acceptedLanguages[language][
    text as unknown as TranslationIndices
  ] as string | undefined;

  return (
    translation?.split(' ').map(parameterize(params)).join(' ') ?? undefined
  );
}

export function useGetLanguage() {
  const { language } = useSettingsService();
  return language || 'de';
}

function useTranslate() {
  const { language, loadSettingsFromStorage } = useSettingsService();
  useEffect(() => {
    if (!language) {
      loadSettingsFromStorage();
    }
  }, [language]);
  return <T>(text: T, params?: Parameters) =>
    translate(text, language || 'de', params);
}

export default useTranslate;

export function useWeekdayAbbreviation() {
  const { language, loadSettingsFromStorage } = useSettingsService();
  useEffect(() => {
    if (!language) {
      loadSettingsFromStorage();
    }
  }, [language]);

  return (date: Date) => {
    if (!date) return null;
    return `${acceptedAbbreviation[language || 'de'][date.getDay()]} ${
      date.getDate() < 10 ? '0' : ''
    }${date.getDate()}.`;
  };
}

export function useMonthAndYear() {
  const { language, loadSettingsFromStorage } = useSettingsService();
  useEffect(() => {
    if (!language) {
      loadSettingsFromStorage();
    }
  }, [language]);

  return (date: Date) => {
    if (!date) return null;
    return `${
      acceptedMonth[language || 'de'][date.getMonth()]
    } ${date.getFullYear()}`;
  };
}
